/**
 * The six sections of work on one project, defined once.
 *
 * This module exists because the two navigation surfaces disagreed. The
 * sidebar built six entries from `paths`; the project header kept its own
 * array of three, written with *relative* router paths (`.`, `cekimler`,
 * `kapsama`) so it did not even share a spelling of the routes. Adding
 * reconstruction, the model viewer and the files screen updated one array and
 * not the other, and nothing failed - the header simply stopped mentioning
 * half the product, which is the kind of gap only a person notices.
 *
 * So both surfaces now map over `PROJECT_SECTIONS`, and a seventh section is
 * one entry here rather than two edits somebody has to remember.
 *
 * `n` is the step number the sidebar prints. It is presentation, not identity:
 * `key` is what tests and code refer to.
 *
 * `end` marks the section whose route is a prefix of every other one. Only the
 * overview has it - `/projeler/:id` matches `/projeler/:id/kapsama` unless the
 * match is exact - and getting it wrong makes the overview look permanently
 * active while you are somewhere else.
 */

import type { ComponentType } from "react";
import {
  IconCube,
  IconFiles,
  IconGrid,
  IconLayers,
  IconTarget,
} from "@/components/icons";
import { paths } from "@/app/paths";

export interface ProjectSection {
  key: string;
  /** Step number, shown by the sidebar. Presentation only. */
  n: string;
  label: string;
  /**
   * Used by the horizontal rail when six full labels will not fit.
   *
   * Only two sections have one, because only two are long enough to matter.
   * A short label that differs from the long one for no reason makes the two
   * surfaces read as different destinations.
   */
  shortLabel?: string;
  icon: ComponentType<{ size?: number }>;
  /** Whether this route must match exactly. True only for the overview. */
  end: boolean;
  path: (projectId: string) => string;
}

export const PROJECT_SECTIONS: readonly ProjectSection[] = [
  {
    key: "overview",
    n: "01",
    label: "Genel Bakış",
    icon: IconLayers,
    end: true,
    path: paths.project,
  },
  {
    key: "sessions",
    n: "02",
    label: "Çekimler",
    icon: IconGrid,
    end: false,
    path: paths.projectSessions,
  },
  {
    key: "coverage",
    n: "03",
    label: "Kapsama",
    icon: IconTarget,
    end: false,
    path: paths.projectCoverage,
  },
  {
    key: "reconstruction",
    n: "04",
    label: "3D Rekonstrüksiyon",
    shortLabel: "Rekonstrüksiyon",
    icon: IconGrid,
    end: false,
    path: paths.projectReconstruction,
  },
  {
    key: "model",
    n: "05",
    label: "3D Model",
    icon: IconCube,
    end: false,
    path: paths.projectModel,
  },
  {
    key: "files",
    n: "06",
    label: "Dosyalar / Çıktılar",
    shortLabel: "Dosyalar",
    icon: IconFiles,
    end: false,
    path: paths.projectFiles,
  },
] as const;
